import { useContext, useState } from 'react';
import { SiteContext } from '../../../context/SiteContext';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { setCountryCode } from '../../common/HelperMethods';
import { PageAbsolutePath } from '../../../consts/Common';
import {
  ApiFile,
  InsuranceJourneyContext,
  NCDEmailContent,
  PaymentMethod
} from '../../common/CommonTypes';
import { FilePondFile } from 'filepond';
import { FileUploadApi } from '../../../api/FileUploadApi';
import { SaveResponse } from './ProofOfNcd';
import UploadNCDDialog from './UploadNCDDialog';
import ViewDocument from '../../common/ViewDocument/ViewDocument';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import InsuranceDocumentLink from './InsuranceDocumentLink';
import BusinessTermsAncillaryPage from './BusinessTermsAncillaryPage';
import PopupComponent from '../../common/PopupComponent';

const PolicyConfirmationPage = (): JSX.Element => {
  const { site } = useContext(SiteContext);
  const navigate = useNavigate();

  const [showUploadModal, setShowUploadModal] = useState(false);

  const [showPopupComponent, setShowPopupComponent] = useState(false);

  const [file, setFile] = useState<FilePondFile | undefined>(undefined);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string>('');

  const { journey } = useOutletContext<InsuranceJourneyContext>();

  const {
    coverDetails,
    policyNumber,
    personalDetails,
    addressDetails,
    insuranceProducts,
    paymentDetails,
    confirmationDetails
  } = journey;

  const { firstName, lastName, email } = personalDetails;

  const {
    overnightParkedAddress,
    correspondenceAddress,
    isThisCorrespondenceAddress
  } = addressDetails;

  const { postCode } = isThisCorrespondenceAddress
    ? overnightParkedAddress
    : correspondenceAddress;

  const { brand } = site;

  const { brandDisplayFullName, brandPhoneNumbers, brandName } = brand;

  const { quotesAndRenewalsNumber, claimsNumber } = brandPhoneNumbers;

  const { coverStartDate } = coverDetails;

  const {
    policyDocumentId,
    ipidStatementDocumentId,
    showNCDDocument,
    optionalProductList
  } = insuranceProducts;

  const getDateString = (date?: Date) => {
    if (date === undefined) return '';
    if (CommonValidation.isDateNull(date, false)) return '';
    if (CommonValidation.isEmpty(date)) return '';

    return new Date(date).toLocaleDateString('en-GB');
  };

  const coverStartDisplayDate = getDateString(coverStartDate);

  const token = site.user.token;

  const onBackClick = () => {
    const path: string = setCountryCode(
      PageAbsolutePath.Insurance.Landing,
      site.countryCode
    );
    navigate(path);
  };

  const onFileChange = (files: FilePondFile[]) => {
    const uploadedFile = files && files.length > 0 ? files[0] : undefined;

    setFile(uploadedFile);
  };

  const onUploadClick = () => {
    setShowUploadModal(true);
  };

  const onCloseModal = () => {
    setShowUploadModal(false);
  };

  const saveFileSuccess = (response: SaveResponse) => {
    if (response.success === false) {
      console.log(`Upload file error: ${response.errorMessage}`);
    } else {
      console.log(`File uploaded`);

      setFile(undefined);
      setShowUploadModal(false);
    }
  };

  const saveFileFailure = () => {
    setSaveErrorMessage('The uploaded file failed to upload, please try again');
  };

  const onUploadModal = () => {
    const saveFiles = () => {
      const apiFile: ApiFile = {
        filename: file?.filename ?? '',
        filepath: file?.serverId ?? '',
        filetype: file?.fileType ?? ''
      };

      const request: NCDEmailContent = {
        firstName: firstName,
        lastName: lastName,
        email: email,
        postCode: postCode ?? '',
        brandName: brand.brandDisplayName,
        attachment: apiFile
      };

      new FileUploadApi().saveFiles(
        request,
        token,
        saveFileSuccess,
        saveFileFailure
      );
    };

    if (file !== undefined) {
      setSaveErrorMessage('');
      saveFiles();
    }
    console.log('saved');
  };

  const policyStartText = `Your policy number ${policyNumber} will start on ${coverStartDisplayDate}`;
  const welcomeText = `Your Welcome Pack${paymentDetails.selectedPaymentMethod === PaymentMethod.annually ? '' : ' and Finance Documents'} will arrive shortly by ${confirmationDetails.yourDocuments?.toLocaleLowerCase()}`;
  const arrangementFeeText =
    site.brand.showArrangementFeeMessage === true
      ? ' Please note that our insurance broker arrangement fee of £40.00 is non-refundable regardless of when you cancel. '
      : '';

  const renderBusinessTermsAncillaryPage = () => {
    return <BusinessTermsAncillaryPage isShowingInPopup />;
  };

  const onClosePopup = () => {
    setShowPopupComponent(false);
  };

  return (
    <div className='confirmation-page'>
      <div className='even-space-out flex-row'>
        <ButtonBack
          name='btn-back'
          text='Back to home page'
          onClick={onBackClick}
          additionalClassNames='btn-back'
        />
      </div>
      <h4 className='section-header m1-top'>
        Welcome to {brandDisplayFullName} Insurance.
      </h4>
      <h4 className='section-header'>You're in good hands with us.</h4>
      <h5 className='m1-top'>{policyStartText}</h5>
      <h5 className='m1-top'>{welcomeText}</h5>
      <p className='m1-top'>
        <b>Remember: </b>You have a 14-day period in which you can cancel the
        insurance. If you exercise this right, you will be required to pay for
        your time insured and your insurer may also charge a cancellation fee -
        refer to their policy document below for details. If you choose to
        cancel outside of the 14-day period, you will be required to pay our
        cancellation fee of £40.00 in addition to the cost of your time on cover
        and your insurers cancellation fee {arrangementFeeText} If you cancel
        outside of the 14-day period, any additional products you have arranged
        are non-refundable.
      </p>
      <h4 className='m2-top section-header'>Important policy information</h4>
      <div className='flex-row confirmation-page-documents m2-bottom'>
        <div className='confirmation-page-document'>
          <InsuranceDocumentLink
            documentId={policyDocumentId}
            displayText='Policy document'
            additionalClasses='inherited'
          />
        </div>
        <div className='confirmation-page-document'>
          <InsuranceDocumentLink
            documentId={ipidStatementDocumentId}
            displayText='IPID statement'
            additionalClasses='inherited'
          />
        </div>
        {showNCDDocument && (
          <div className='confirmation-page-document'>
            <ViewDocument
              path={`docs/Guide to NCD.pdf`}
              documentName='Important NCD Information'
              additionalClasses='auto-width inherited'
            />
          </div>
        )}
        <div className='confirmation-page-document'>
          <Link
            id='lnkTermsOfBusinessInText'
            className='in-text inherited'
            onClick={() => setShowPopupComponent(true)}
            to=''>
            Terms of Business
          </Link>
        </div>
        <>
          {optionalProductList
            .filter((op) => op.isOptionalProductSelected === true)
            .map((sp) => {
              return (
                <>
                  {sp.optionalProductPolicyDocumentId && (
                    <div className='confirmation-page-document'>
                      <InsuranceDocumentLink
                        displayText={`${sp.optionalProductName} - Policy Document`}
                        documentId={sp.optionalProductPolicyDocumentId}
                        additionalClasses='inherited'
                      />
                    </div>
                  )}
                  {sp.optionalProductIPIDStatementDocumentId && (
                    <div className='confirmation-page-document'>
                      <InsuranceDocumentLink
                        displayText={`${sp.optionalProductName} - IPID Document`}
                        documentId={sp.optionalProductIPIDStatementDocumentId}
                        additionalClasses='inherited'
                      />
                    </div>
                  )}
                </>
              );
            })}
        </>
      </div>
      <div className='get-in-touch'>
        <h4 className='m1-top section-header'>If you need to get in touch</h4>
        <ol>
          <li className='m1-top'>
            <p>
              Make sure you email your proof of No Claims Discount to{' '}
              <a
                className='inherited'
                href='mailto:protexNCDproof@carcareplan.co.uk'>
                protexNCDproof@carcareplan.co.uk{' '}
              </a>{' '}
              within the next 21 days to make sure your discount is correctly
              applied.
            </p>
            <p className='m1-top'>
              Or to ensure you don't forget, why not upload it now?
            </p>
            <ButtonContinue
              name='btn-upload'
              text='Upload'
              onClick={onUploadClick}
              additionalClassNames='btn-continue m1-top'
            />
            <hr />
          </li>
          <li className='m1-top'>
            Our Customer Service team are here to assist you on{' '}
            {quotesAndRenewalsNumber} Monday to Saturday, 9:00am until 5:30pm.
            <hr />
          </li>
          <li className='m1-top'>
            Now is a good time to save our 24-hour claim number to you phone. If
            you need to make a claim, simply call {claimsNumber} and we'll do
            all we can to help.
          </li>
        </ol>
      </div>
      <UploadNCDDialog
        showDialog={showUploadModal}
        onCloseModal={onCloseModal}
        onUploadModal={onUploadModal}
        brandName={brandName}
        file={file}
        onFileChange={(files: FilePondFile[]) => onFileChange(files)}
        saveErrorMessage={saveErrorMessage}
        token={token}
      />
      <PopupComponent
        showPopupComponent={showPopupComponent}
        renderComponent={renderBusinessTermsAncillaryPage}
        onClosePopup={onClosePopup}
        popupTitle='Terms of Business'
      />
    </div>
  );
};

export default PolicyConfirmationPage;
